import { useEffect, useRef, useState } from 'react';
import { connectSse, type SseConnection, type SseEvent } from '../api/sseClient';
import { refreshAccessToken } from '../api/client';
import type {
  ChatMessageEvent,
  AiActionStatusEvent,
  ChatMessageDeltaEvent,
  ChatMessageCompleteEvent,
} from '../types/chat';

/**
 * useSSE — chat room SSE 구독 React hook (P2-14).
 *
 * 사용:
 *   const { status, lastEventId } = useSSE({
 *     roomId: '100',
 *     onMessage: (msg) => store.appendMessage(msg),
 *     onAiActionStatus: (s) => store.updateAction(s),
 *   });
 *
 * 책임:
 *   - 마운트 시 connectSse(GET /chat/rooms/:id/stream) 호출.
 *   - sseClient 의 onTokenRefreshRequired → authStore.refreshAccessToken (single-flight).
 *   - SseEnvelope 파싱 후 eventType 별 callback 분기 (sse.message / sse.ai_action_status).
 *   - 컴포넌트 unmount 또는 roomId 변경 시 close.
 *
 * 다중 탭 sync: BroadcastChannel('workchat:auth') 의 tokens-updated 가 authStore 갱신
 *   → sseClient 의 다음 reconnect cycle 에서 새 token 사용 (즉시 reconnect 아님).
 *   강제 reconnect 가 필요하면 connection.close() 호출.
 */

export type SseStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'exhausted' | 'closed' | 'unauthorized';

export interface UseSSEOptions {
  /** chat room SSE: roomId 지정 → /chat/rooms/{roomId}/stream. */
  roomId?: string;
  /** user-only SSE: 'me' 지정 → /sse/me (room context 없음, dashboard 외부 도메인). */
  mode?: 'room' | 'me';
  /** chat_messages.commit_seq 로부터 catch-up. 없으면 새 메시지부터. */
  initialLastEventId?: string;
  onMessage?: (msg: ChatMessageEvent) => void;
  onAiActionStatus?: (status: AiActionStatusEvent) => void;
  /** AI 응답 streaming chunk — adr-20260430-2235. backend `sse.ai_delta` (room channel). */
  onAiDelta?: (delta: ChatMessageDeltaEvent) => void;
  /** AI 응답 streaming 종료 — adr-20260430-2235. backend `sse.ai_complete`. */
  onAiComplete?: (complete: ChatMessageCompleteEvent) => void;
  /** AI 도구 사용 lifecycle — orchestrator-svc 가 redis room 채널로 publish. payload: { roomId, phase, tool, iter?, ok? } */
  onAiToolUse?: (e: { roomId: string; phase: 'start' | 'done'; tool: string; iter?: number; ok?: boolean }) => void;
  /** in-app inbox 신규 알림 — backend `sse.notification` (user 채널). dual-trail (DB + 실시간). */
  onNotification?: (notif: import('../types/notification').NotificationEvent) => void;
  /** 채팅 미읽음 실시간 힌트 — backend `sse.chat_unread` (user 채널). 보고 있지 않은 방 새 메시지. */
  onChatUnread?: (e: { roomId: string; senderId: string | null }) => void;
  /**
   * 읽음 영수증 — backend `sse.chat_read` (room 채널). 같은 방 멤버가 읽음 처리한 사실.
   * chat-read-receipts: 수신측이 readStates 를 전진시켜 내 메시지의 미읽음 인원 배지를 재계산.
   */
  onChatRead?: (e: { roomId: string; userId: string; lastReadId: string }) => void;
  /** 메시지 삭제(전체 회수) — backend `sse.message_deleted` (room 채널). 톰스톤으로 갱신. */
  onMessageDeleted?: (e: { roomId: string; messageId: string }) => void;
  /** 방 자동 삭제(나가기 후 사용자 1인+AI 잔류) — backend `sse.room_deleted` (user 채널). 목록에서 제거. */
  onRoomDeleted?: (e: { roomId: string }) => void;
  /** SSE error / disconnect 표시용 (UI 상태 업데이트). */
  onStatusChange?: (status: SseStatus) => void;
  /** falsy 면 connect 안 함 (라우트 전이 중 임시 상태). */
  enabled?: boolean;
}

export interface UseSSEResult {
  status: SseStatus;
  /** 가장 최근 수신 commitSeq. catch-up 시작점 또는 reconnect 보존. */
  lastEventId: string | null;
}

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

// ─────────────────────────────────────────────────────────────────────────────
// iter-3 — dispatch table (sse-bridge handler ROOM_DISPATCHERS / USER_DISPATCHERS
// cross-domain pattern N=2 정착). 9-way 분기 → 2 table lookup. 새 event 추가는
// 1 row 추가만으로 가능 — backend 의 ROOM_DISPATCHERS 와 동형 ergonomics.
//
// **Raw vs Envelope wire 호환 정책 (iter-1 시점):**
// backend sse.handler.ts 는 SSE event name (`event: 'message' | 'ai_action_status'
// | ...`) 으로 분기 표현 — 일반 raw 분기 (RAW_DISPATCHERS) 가 매칭. envelope
// (`{ eventType, payload }`) 는 wire 호환 fallback (legacy / 향후 통일 시 deprecated).
// ─────────────────────────────────────────────────────────────────────────────

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

interface SseHandlers {
  onMessage?: (msg: ChatMessageEvent) => void;
  onAiActionStatus?: (status: AiActionStatusEvent) => void;
  onAiDelta?: (delta: ChatMessageDeltaEvent) => void;
  onAiComplete?: (complete: ChatMessageCompleteEvent) => void;
  onAiToolUse?: (e: { roomId: string; phase: 'start' | 'done'; tool: string; iter?: number; ok?: boolean }) => void;
  onNotification?: (notif: import('../types/notification').NotificationEvent) => void;
  onChatUnread?: (e: { roomId: string; senderId: string | null }) => void;
  onChatRead?: (e: { roomId: string; userId: string; lastReadId: string }) => void;
  onMessageDeleted?: (e: { roomId: string; messageId: string }) => void;
  onRoomDeleted?: (e: { roomId: string }) => void;
}

/** Raw dispatcher — `event:` name 으로 lookup. payload 검증 후 dispatch. true=처리됨, false=다음 단계. */
type RawDispatcher = (parsed: Record<string, unknown>, handlers: SseHandlers) => boolean;

const RAW_DISPATCHERS: Record<string, RawDispatcher> = {
  // event: 'ai_action_status' — raw payload (actionId 검증).
  ai_action_status: (parsed, h) => {
    if (typeof parsed.actionId !== 'string') return false;
    h.onAiActionStatus?.(parsed as unknown as AiActionStatusEvent);
    return true;
  },
  // event: 'ai_delta' — adr-20260430-2235 streaming chunk.
  ai_delta: (parsed, h) => {
    if (typeof parsed.streamMessageId !== 'string') return false;
    h.onAiDelta?.(parsed as unknown as ChatMessageDeltaEvent);
    return true;
  },
  // event: 'ai_complete' — streaming 종료.
  ai_complete: (parsed, h) => {
    if (typeof parsed.streamMessageId !== 'string') return false;
    h.onAiComplete?.(parsed as unknown as ChatMessageCompleteEvent);
    return true;
  },
  // event: 'ai_tool_use' — orchestrator-svc 가 도구 사용 진행/완료 시 발화.
  ai_tool_use: (parsed, h) => {
    if (typeof parsed.roomId !== 'string' || typeof parsed.tool !== 'string') return false;
    if (parsed.phase !== 'start' && parsed.phase !== 'done') return false;
    h.onAiToolUse?.(parsed as unknown as { roomId: string; phase: 'start' | 'done'; tool: string; iter?: number; ok?: boolean });
    return true;
  },
  // event: 'message' (또는 ev.event undefined 의 default) — raw chat message.
  message: (parsed, h) => {
    if (typeof parsed.commitSeq !== 'string') return false;
    h.onMessage?.(parsed as unknown as ChatMessageEvent);
    return true;
  },
  // event: 'notification' — backend sseUserStream raw payload (notificationId + userId).
  notification: (parsed, h) => {
    if (typeof parsed.notificationId !== 'string' || typeof parsed.userId !== 'string') return false;
    h.onNotification?.(parsed as unknown as import('../types/notification').NotificationEvent);
    return true;
  },
  // event: 'chat_unread' — backend sse.chat_unread (user 채널). 미읽음 배지 실시간 갱신.
  chat_unread: (parsed, h) => {
    if (typeof parsed.roomId !== 'string') return false;
    h.onChatUnread?.(parsed as unknown as { roomId: string; senderId: string | null });
    return true;
  },
  // event: 'chat_read' — chat-read-receipts. 같은 방 멤버의 lastReadId 전진. 미읽음 인원 배지 갱신.
  chat_read: (parsed, h) => {
    if (typeof parsed.roomId !== 'string' || typeof parsed.userId !== 'string') return false;
    if (typeof parsed.lastReadId !== 'string') return false;
    h.onChatRead?.(parsed as unknown as { roomId: string; userId: string; lastReadId: string });
    return true;
  },
  // event: 'message_deleted' — chat-message-delete 전체 회수. 해당 메시지를 톰스톤으로 갱신.
  message_deleted: (parsed, h) => {
    if (typeof parsed.roomId !== 'string' || typeof parsed.messageId !== 'string') return false;
    h.onMessageDeleted?.(parsed as unknown as { roomId: string; messageId: string });
    return true;
  },
  // event: 'room_deleted' — 나가기 후 사용자 1인+AI 잔류 시 방 자동 삭제. 목록에서 제거.
  room_deleted: (parsed, h) => {
    if (typeof parsed.roomId !== 'string') return false;
    h.onRoomDeleted?.(parsed as unknown as { roomId: string });
    return true;
  },
};

/** Envelope dispatcher — `eventType:` 으로 lookup. payload 가 plain object 임을 caller 가 검증. */
type EnvelopeDispatcher = (payload: Record<string, unknown>, handlers: SseHandlers) => void;

const ENVELOPE_DISPATCHERS: Record<string, EnvelopeDispatcher> = {
  'sse.message': (payload, h) => { h.onMessage?.(payload as unknown as ChatMessageEvent); },
  'sse.ai_action_status': (payload, h) => { h.onAiActionStatus?.(payload as unknown as AiActionStatusEvent); },
  'sse.notification': (payload, h) => {
    h.onNotification?.(payload as unknown as import('../types/notification').NotificationEvent);
  },
  'sse.chat_unread': (payload, h) => {
    h.onChatUnread?.(payload as unknown as { roomId: string; senderId: string | null });
  },
  'sse.chat_read': (payload, h) => {
    if (typeof payload.roomId !== 'string' || typeof payload.userId !== 'string') return;
    if (typeof payload.lastReadId !== 'string') return;
    h.onChatRead?.(payload as unknown as { roomId: string; userId: string; lastReadId: string });
  },
  'sse.message_deleted': (payload, h) => {
    if (typeof payload.roomId !== 'string' || typeof payload.messageId !== 'string') return;
    h.onMessageDeleted?.(payload as unknown as { roomId: string; messageId: string });
  },
  'sse.room_deleted': (payload, h) => {
    if (typeof payload.roomId !== 'string') return;
    h.onRoomDeleted?.(payload as unknown as { roomId: string });
  },
  'sse.ai_tool_use': (payload, h) => {
    if (typeof payload.roomId !== 'string' || typeof payload.tool !== 'string') return;
    if (payload.phase !== 'start' && payload.phase !== 'done') return;
    h.onAiToolUse?.(payload as unknown as { roomId: string; phase: 'start' | 'done'; tool: string; iter?: number; ok?: boolean });
  },
};

/**
 * SSE event 분기 처리. raw → envelope 순차 lookup. 매칭 안 되면 silent drop.
 * - parsed 가 plain object 가 아니면 즉시 false 반환 (caller 가 isPlainObject 호출 권장하나 안전 가드).
 * - ev.event 가 undefined 인 경우 'message' 로 normalize (raw chat message default).
 */
function dispatchSseEvent(ev: SseEvent, parsed: unknown, handlers: SseHandlers): void {
  if (!isPlainObject(parsed)) return;

  // Raw 분기 — ev.event ?? 'message' (undefined 면 raw chat message default).
  const rawKey = ev.event ?? 'message';
  const rawDispatcher = RAW_DISPATCHERS[rawKey];
  if (rawDispatcher && rawDispatcher(parsed, handlers)) return;

  // Envelope 분기 — eventType + payload 검증.
  if (typeof parsed.eventType === 'string' && isPlainObject(parsed.payload)) {
    const envelopeDispatcher = ENVELOPE_DISPATCHERS[parsed.eventType];
    if (envelopeDispatcher) {
      envelopeDispatcher(parsed.payload, handlers);
    }
  }
  // 매칭 실패 — silent drop (backend sse.handler 의 unknown eventType debug log 와 동형 정책).
}

export function useSSE(opts: UseSSEOptions): UseSSEResult {
  const [status, setStatus] = useState<SseStatus>('idle');
  const [lastEventId, setLastEventId] = useState<string | null>(opts.initialLastEventId ?? null);

  // ref 로 callback 캡처 — 의존성 변경으로 connection 재생성 회피.
  const onMessageRef = useRef(opts.onMessage);
  const onAiActionStatusRef = useRef(opts.onAiActionStatus);
  const onAiDeltaRef = useRef(opts.onAiDelta);
  const onAiCompleteRef = useRef(opts.onAiComplete);
  const onAiToolUseRef = useRef(opts.onAiToolUse);
  const onNotificationRef = useRef(opts.onNotification);
  const onChatUnreadRef = useRef(opts.onChatUnread);
  const onChatReadRef = useRef(opts.onChatRead);
  const onMessageDeletedRef = useRef(opts.onMessageDeleted);
  const onRoomDeletedRef = useRef(opts.onRoomDeleted);
  const onStatusChangeRef = useRef(opts.onStatusChange);
  const lastEventIdRef = useRef(lastEventId);
  onMessageRef.current = opts.onMessage;
  onAiActionStatusRef.current = opts.onAiActionStatus;
  onAiDeltaRef.current = opts.onAiDelta;
  onAiCompleteRef.current = opts.onAiComplete;
  onAiToolUseRef.current = opts.onAiToolUse;
  onNotificationRef.current = opts.onNotification;
  onChatUnreadRef.current = opts.onChatUnread;
  onChatReadRef.current = opts.onChatRead;
  onMessageDeletedRef.current = opts.onMessageDeleted;
  onRoomDeletedRef.current = opts.onRoomDeleted;
  onStatusChangeRef.current = opts.onStatusChange;

  useEffect(() => {
    if (opts.enabled === false) {
      return;
    }
    // mode=me 는 roomId 불필요. 그 외 (default 'room') 는 roomId 필수.
    if (opts.mode !== 'me' && !opts.roomId) {
      return;
    }

    let connection: SseConnection | null = null;
    // effect-local cancelled flag (review batch-15 LOW-1 봉합) — cleanup 직후 buffered
    // event 가 dispatch 되어 다른 room 으로 잘못 라우팅되는 micro-window race 차단.
    let cancelled = false;
    const updateStatus = (s: SseStatus) => {
      if (cancelled) return;
      setStatus(s);
      onStatusChangeRef.current?.(s);
    };

    updateStatus('connecting');
    const sseUrl = opts.mode === 'me'
      ? `${baseURL}/sse/me`
      : `${baseURL}/chat/rooms/${opts.roomId}/stream`;
    connection = connectSse({
      url: sseUrl,
      // push-presence-cellular — SSE 수명 동안 presence 하트비트. 없으면 서버가 이 사용자를
      // "오프라인" 으로 보고 웹 사용 중에도 폰으로 푸시를 보낸다(회귀). 상세: api/presence.ts.
      presenceBaseUrl: baseURL,
      // auth v1.5 — fetch 가 cookie 자동 동봉 (sseClient credentials:'include').
      initialLastEventId: lastEventIdRef.current ?? undefined,
      onTokenRefreshRequired: async () => {
        // single-flight refresh 재사용 — axios interceptor 와 같은 promise.
        await refreshAccessToken();
      },
      onConnect: () => updateStatus('open'),
      onDisconnect: (reason) => {
        if (reason === 'closed') updateStatus('closed');
        else if (reason === 'exhausted') updateStatus('exhausted');
        else if (reason === 'unauthorized' || reason.startsWith('unauthorized:')) {
          // refresh 도 실패 — 사용자 cookie 영구 invalid. 짧은 안내 후 로그인 페이지 redirect.
          updateStatus('unauthorized');
          window.setTimeout(() => {
            window.location.href = `/login?reason=session_expired&next=${encodeURIComponent(window.location.pathname)}`;
          }, 1800);
        }
        else updateStatus('reconnecting');
      },
      onEvent: (ev: SseEvent) => {
        if (cancelled) return; // unmount 후 buffered event 라우팅 차단.
        if (ev.id) {
          lastEventIdRef.current = ev.id;
          setLastEventId(ev.id);
        }
        if (!ev.data) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(ev.data);
        } catch {
          return; // malformed — drop.
        }
        // iter-3 — dispatch table lookup (raw → envelope, sse-bridge handler 동형 cross-domain N=2).
        dispatchSseEvent(ev, parsed, {
          onMessage: onMessageRef.current,
          onAiActionStatus: onAiActionStatusRef.current,
          onAiDelta: onAiDeltaRef.current,
          onAiComplete: onAiCompleteRef.current,
          onAiToolUse: onAiToolUseRef.current,
          onNotification: onNotificationRef.current,
          onChatUnread: onChatUnreadRef.current,
          onChatRead: onChatReadRef.current,
          onMessageDeleted: onMessageDeletedRef.current,
          onRoomDeleted: onRoomDeletedRef.current,
        });
      },
    });

    return () => {
      cancelled = true;
      connection?.close();
    };
    // roomId / enabled 만 의존성 — callback 변경 시 reconnect 안 함.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.roomId, opts.mode, opts.enabled]);

  return { status, lastEventId };
}

// iter-3 — 단위 테스트용 export (sse.handler.ts __test__ 와 동형 ergonomics).
export const __test__ = {
  RAW_DISPATCHERS,
  ENVELOPE_DISPATCHERS,
  dispatchSseEvent,
  isPlainObject,
};
